import { useEffect, useRef, useCallback, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X } from '@phosphor-icons/react';
import { pushModal, lockBodyScroll } from '../utils/modalStack';
import telegramService from '../services/telegram';

/**
 * Единая всплывающая панель. Брендбук, раздел 7.
 *
 * Способы закрытия — крестик (основной), кнопка «Назад» Telegram, Escape,
 * тап по затемнению у панелей без ввода. Свайп вниз намеренно не реализован:
 * внутри Telegram он конфликтует со свайпом сворачивания приложения (7.2).
 */

// Элементы, на которые можно поставить фокус — для ловушки фокуса
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Сколько панель остаётся в разметке после закрытия — длительность
// анимации ухода, токен `exit` (брендбук 11)
const EXIT_MS = 200;

const BottomSheet = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  // Значения по умолчанию — здесь, а не в `defaultProps`: React 19 их
  // у функциональных компонентов игнорирует. Из-за этого `maxHeight`
  // нигде не применялся — длинная панель (мастер маршрута на 7 мест)
  // вырастала выше экрана и не прокручивалась, а тап мимо не закрывал
  // ни одну панель.
  //
  // У панелей с вводом тап мимо закрывает слишком легко — теряется набранное
  closeOnBackdrop = true,
  // Ручка по факту не рисовалась (defaultProps не работал) — так и оставляем:
  // панели в приложении давно живут без неё, крестик главный (брендбук 7.3)
  showHandle = false,
  // Не 92vh: внутри Telegram vh не равен видимой области, панель уезжает
  // за верхнюю кромку вместе с заголовком. Реальную высоту клиент отдаёт
  // в --tg-viewport-height, вне Telegram переменная равна 100vh (брендбук 7.2).
  // Сверху вычитаем ещё отступ под кнопки Telegram в полноэкранном режиме
  maxHeight = 'calc((var(--tg-viewport-height, 100vh) - var(--safe-area-top, 0px) - var(--content-safe-area-top, 0px)) * 0.94)',
  contentClassName = '',
}) => {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const titleId = useId();

  // Панель живёт в разметке ещё EXIT_MS после закрытия — пока играет
  // анимация ухода. Раньше `!isOpen` сразу возвращал null, и панель,
  // въехавшая плавно, исчезала рывком
  const [rendered, setRendered] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      setRendered(true);
      return undefined;
    }
    const timer = setTimeout(() => setRendered(false), EXIT_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);
  const closing = !isOpen;

  const handleClose = useCallback(() => {
    telegramService.hapticFeedback('light');
    onClose();
  }, [onClose]);

  // Регистрация в стеке: пока панель открыта, «Назад» закрывает её,
  // а не уводит со страницы
  useEffect(() => {
    if (!isOpen) return undefined;
    return pushModal(onClose);
  }, [isOpen, onClose]);

  // Escape закрывает, скролл body блокируется
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      // Ловушка фокуса: Tab по кругу внутри панели
      const items = panelRef.current.querySelectorAll(FOCUSABLE);
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Со счётчиком: панель поверх панели не должна вернуть скролл фону,
    // пока открыта хотя бы одна
    const unlockScroll = lockBodyScroll();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      unlockScroll();
    };
  }, [isOpen, handleClose]);

  // Фокус внутрь панели при открытии, возврат на триггер при закрытии
  useEffect(() => {
    if (!isOpen) return undefined;

    triggerRef.current = document.activeElement;
    const focusTarget = panelRef.current?.querySelector(FOCUSABLE);
    focusTarget?.focus();

    return () => {
      // Триггер мог исчезнуть вместе с содержимым — тогда возвращать некуда
      if (triggerRef.current?.isConnected) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen && !rendered) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      handleClose();
    }
  };

  return createPortal(
    <div
      className={`fixed inset-x-0 top-0 z-scrim flex items-end justify-center ${
        closing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      style={{
        // Уходящая панель не ловит касания: тап сквозь неё попадает
        // в страницу, а не в повторное закрытие
        pointerEvents: closing ? 'none' : undefined,
        background: 'var(--color-scrim)',
        // Не inset-0: `bottom: 0` растягивает подложку на 100vh, а внутри
        // Telegram vh больше видимой области (брендбук 7.2). Панель
        // прижата к низу этой завышенной коробки, поэтому её верх уходил
        // за кромку экрана — на шаге 2 визарда карточки было не достать.
        // maxHeight панели считался правильно, а контейнер вокруг — нет
        height: 'var(--tg-viewport-height, 100vh)',
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Всплывающая панель'}
        // max-w-md (448px) — ширина колонки приложения, нижнего меню
        // и панели прогулки. Был max-w-app (428px): на экране шире
        // панель выходила уже всего остального, с полосами по бокам
        className={`w-full max-w-md flex flex-col ${closing ? 'animate-slideDown' : 'animate-slideUp'}`}
        style={{
          background: 'var(--color-card)',
          // Граница обязательна: в светлой теме card и bg оба белые (брендбук 2.8)
          border: '1px solid var(--color-border)',
          borderBottom: 'none',
          borderRadius: 'var(--radius-2xl) var(--radius-2xl) 0 0',
          maxHeight,
          // Тот же расчёт, что у нижнего меню (BottomNavigation.css): максимум
          // из базовых 20px, врезки от Telegram и системной. Раньше
          // системной не было — а на Android с кнопочной навигацией Telegram
          // присылает в --safe-area-bottom ноль, и кнопки панели уходили
          // под системную панель «назад / домой». Меню при этом стояло
          // правильно: env() у него был
          // К врезке +12px: на iPhone кнопки вставали вплотную к полоске
          // «домой» — формально над ней, но нажимать было неудобно
          paddingBottom: 'max(var(--space-5), calc(var(--safe-area-bottom, 0px) + 12px), calc(env(safe-area-inset-bottom, 0px) + 12px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showHandle && (
          // Признак панели, не кнопка: ничего не закрывает (брендбук 7.2)
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0" aria-hidden="true">
            <div
              className="w-9 h-1 rounded-full"
              style={{ background: 'var(--color-border-strong)', opacity: 0.5 }}
            />
          </div>
        )}

        <div
          className={`flex items-start justify-between gap-3 px-5 flex-shrink-0 ${
            showHandle ? 'pt-2' : 'pt-5'
          }`}
        >
          <div className="min-w-0 flex-1">
            {title && (
              <h2 id={titleId} className="text-h2 text-theme-text-primary">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-body-sm text-theme-text-secondary mt-1">{subtitle}</p>
            )}
          </div>

          {/* Тап-зона 44×44 при иконке 20px — добирается отступом (брендбук 9.3) */}
          <button
            type="button"
            onClick={handleClose}
            aria-label="Закрыть"
            className="flex-shrink-0 -mr-2 -mt-1 w-11 h-11 flex items-center justify-center rounded-full transition-colors duration-fast ease-out active:scale-97"
            style={{ background: 'var(--color-card-secondary)' }}
          >
            <X size={20} weight="bold" style={{ color: 'var(--color-text-secondary)' }} />
          </button>
        </div>

        {/* min-h-0 обязателен: у flex-элемента min-height по умолчанию auto,
            то есть контейнер отказывается сжиматься меньше содержимого.
            Длинный список (шаг 2 визарда) распирал панель выше maxHeight,
            верхние карточки уезжали за кромку экрана и нажать их было
            нельзя, а overflow-y тут не срабатывал вовсе */}
        <div className={`px-5 pt-4 overflow-y-auto flex-1 min-h-0 ${contentClassName}`}>{children}</div>

        {footer && (
          <div className="px-5 pt-3 flex-shrink-0">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
};

BottomSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  closeOnBackdrop: PropTypes.bool,
  showHandle: PropTypes.bool,
  maxHeight: PropTypes.string,
  contentClassName: PropTypes.string,
};

export default BottomSheet;
